<template>
  <section class="form-list">
    <div v-show="isLoading || isSaving || isLoadingError" class="loading-box">
      <div class="loading-main" @click="load">
        <svg v-show="isLoading || isSaving" class="loading-circular" viewBox="25 25 50 50">
          <circle class="loading-path" cx="50" cy="50" r="20" fill="none"/>
        </svg>
        <div v-show="isLoadingError" class="loading-refresh">
          <i class="el-icon-refresh"/>
        </div>
        <div v-show="isLoadingError" class="loading-text">
          数据加载失败，点击重新尝试
        </div>
      </div>
    </div>

    <el-form
      ref="form"
      :model="report"
      label-position="left"
      label-width="160px"
      class="form form-max"
    >
      <el-form-item label="所属月份">
        {{ getYearAndMonth(report.report_date) }}
      </el-form-item>

      <div class="table-box">
        <h3>门店收入提成</h3>
        <el-table
          :data="storeIncomes"
          class="income-table store-income-table"
          border
          fit
          highlight-current-row
          :show-summary="true"
          :summary-method="getStoreSummaries"
        >
          <el-table-column label="门店" align="center">
            <template v-slot="{ row }">
              {{ row.store_name }}
            </template>
          </el-table-column>
          <el-table-column label="总收入" align="center" prop="all_income">
            <template v-slot="{ row }">
              {{ row.all_income }}
            </template>
          </el-table-column>
          <el-table-column label="总利润" align="center" prop="profit">
            <template v-slot="{ row }">
              {{ row.profit }}
            </template>
          </el-table-column>
          <el-table-column label="提成百分比" align="center">
            <template v-slot="{ row }">
              {{ row.revenue_sharing }} %
            </template>
          </el-table-column>
          <el-table-column label="提成" align="center" prop="commission">
            <template v-slot="{ row }">
              {{ row.commission }}
            </template>
          </el-table-column>
          <el-table-column label="提成占利润比" align="center">
            <template v-slot="{ row }">
              {{ row.profit ? `${(row.commission / row.profit * 100).toFixed(2)} %` : '' }}
            </template>
          </el-table-column>
        </el-table>
      </div>

      <div class="table-box">
        <h3>员工工资</h3>
        <el-table
          :data="staffSalaries"
          class="income-table"
          border
          fit
          highlight-current-row
          :show-summary="true"
          :summary-method="getStaffSummaries"
        >
          <el-table-column label="员工" align="center" width="154">
            <template v-slot="{ row }">
              <v-avatar-nickname :value="row.avatar" :nickname="row.nickname"/>
            </template>
          </el-table-column>
          <el-table-column label="底薪" align="center" prop="base_pay">
            <template v-slot="{ row }">
              {{ row.base_pay || '-' }}
            </template>
          </el-table-column>
          <el-table-column label="提成" align="center" prop="percentage_pay">
            <template v-slot="{ row }">
              {{ row.percentage_pay || '-' }}
            </template>
          </el-table-column>
          <el-table-column label="公司社保" align="center" prop="social_insurance">
            <template v-slot="{ row }">
              {{ row.social_insurance || '-' }}
            </template>
          </el-table-column>
          <el-table-column label="个人社保" align="center" prop="personal_insurance">
            <template v-slot="{ row }">
              {{ row.personal_insurance || '-' }}
            </template>
          </el-table-column>
          <el-table-column label="奖金" align="center" prop="bonus">
            <template v-slot="{ row }">
              {{ row.bonus || '-' }}
            </template>
          </el-table-column>
          <el-table-column label="实发" align="center" prop="actual_pay">
            <template v-slot="{ row }">
              {{ row.actual_pay }}
            </template>
          </el-table-column>
          <el-table-column label="总计" align="center" prop="all_pay">
            <template v-slot="{ row }">
              <span class="success-color">{{ row.all_pay }}</span>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <div class="table-box">
        <h3>收入统计</h3>
        <el-form-item label="提成店数量">
          <span class="primary-color money">{{ report.store_number }}</span> 个
        </el-form-item>
        <el-form-item label="门店提成收入">
          <span class="danger-color money">{{ report.store_income }}</span> 元
        </el-form-item>
        <el-form-item label="大师课数量">
          <span class="primary-color money">{{ report.master_number }}</span> 节
        </el-form-item>
        <el-form-item label="大师课收入">
          <span class="danger-color money">{{ report.master_income }}</span> 元
        </el-form-item>
        <el-form-item label="有赞收入">
          <span class="danger-color money">{{ report.you_zan_income }}</span> 元
        </el-form-item>
        <el-form-item label="其他收入">
          <span class="danger-color money">{{ report.other_income }}</span> 元
        </el-form-item>
        <el-form-item label="其他收入备注">
          {{ report.other_income_remark }}
        </el-form-item>
      </div>

      <div class="table-box">
        <h3>支出统计</h3>
        <el-form-item label="房租支出">
          <span class="success-color money">{{ report.house_pay }}</span> 元
        </el-form-item>
        <el-form-item label="员工工资支出">
          <span class="success-color money">{{ report.staff_pay }}</span> 元
        </el-form-item>
        <el-form-item label="保洁工资支出">
          <span class="success-color money">{{ report.cleaner_pay }}</span> 元
        </el-form-item>
        <el-form-item label="水电费支出">
          <span class="success-color money">{{ report.water_pay }}</span> 元
        </el-form-item>
        <el-form-item label="有赞核销支出">
          <span class="success-color money">{{ report.you_zan_pay }}</span> 元
        </el-form-item>
        <el-form-item label="其他支出">
          <span class="success-color money">{{ report.other_pay }}</span> 元
        </el-form-item>
        <el-form-item label="其他支出备注">
          {{ report.other_pay_remark }}
        </el-form-item>
        <el-form-item label="余额">
          <span class="success-color money">{{ report.balance }}</span> 元
        </el-form-item>
        <el-form-item label="加盟店押金">
          <span class="success-color money">{{ report.store_deposit }}</span> 元
        </el-form-item>
      </div>

      <div class="table-box">
        <div class="flex-align-center">
          <h3 class="profit-title">总收入</h3>
          <h2 class="danger-color">{{ report.all_income }}</h2>
        </div>
        <div class="flex-align-center">
          <h3 class="profit-title">总支出</h3>
          <h2 class="success-color">{{ report.expenditure }}</h2>
        </div>
        <div class="flex-align-center">
          <h3 class="profit-title">总利润</h3>
          <h2 :class="`${report.profit >= 0 ? 'danger' : 'success'}-color`">{{ report.profit }}</h2>
        </div>
      </div>

      <el-button @click="RouteConfig.officeMonthlyReportList.back">返回</el-button>
    </el-form>
  </section>
</template>

<script>
import RouteConfig from '@/router/config';
import { mapGetters } from 'vuex';
import {
  show,
  getStaffSalariesByReportId,
  getStoreIncomesByReportId,
} from '@/api/office-monthly-report';
import { getSummaries } from "@/views/office-monthly-report/getSummary";
import { pickerOptionsOfAfterBeforeToday } from '@/utils/formats';
import { toMillisecond, toYuan } from "@/filters";

export default {
  name: RouteConfig.officeMonthlyReportDetail.name,
  data: () => ({
    RouteConfig,
    pickerOptionsOfAfterBeforeToday,
    isLoading: false,
    isLoadingError: false,
    isSaving: false,
    staffSalaries: [],
    storeIncomes: [],
    report: {
      report_date: '',
      store_number: 0,
      store_income: 0,
      master_number: 0,
      master_income: 0,
      you_zan_income: 0,
      other_income: 0,
      other_income_remark: '',
      all_income: 0,
      house_pay: 0,
      staff_pay: 0,
      cleaner_pay: 0,
      water_pay: 0,
      you_zan_pay: 0,
      other_pay: 0,
      other_pay_remark: '',
      expenditure: 0,
      balance: 0,
      profit: 0,
      store_deposit: 0,
    },
  }),
  computed: {
    ...mapGetters([
      'isAdministrator',
      'userStoreId',
    ]),
    id() {
      return +this.$route.params.id || 0;
    },
  },
  watch: {
    'report.store_income'() {
      this.computeAllIncome();
    },
    'report.master_income'() {
      this.computeAllIncome();
    },
    'report.other_income'() {
      this.computeAllIncome();
    },
  },
  beforeMount() {
    this.load();
  },
  methods: {
    getYearAndMonth(timestamp) {
      if (!timestamp) return '';

      const date = new Date(timestamp);
      const month = date.getMonth() + 1;
      return `${date.getFullYear()} 年 ${month} 月`;
    },

    getStaffSummaries(param) {
      return getSummaries({
        param,
        report: this.report,
        allPayField: 'staff_pay',
      });
    },
    getStoreSummaries(param) {
      return getSummaries({
        param,
        report: this.report,
        allPayField: 'store_income',
      });
    },
    async show() {
      const { isError, data } = await show(this.id);
      if (isError) return this.isLoadingError = true;

      Object.assign(this.report, {
        report_date: toMillisecond(data.report_date),
        store_number: data.store_number || 0,
        store_income: toYuan(data.store_income),
        master_number: data.master_number || 0,
        master_income: toYuan(data.master_income),
        you_zan_income: toYuan(data.you_zan_income),
        other_income: toYuan(data.other_income),
        other_income_remark: data.other_income_remark || '',
        all_income: toYuan(data.all_income),
        house_pay: toYuan(data.house_pay),
        staff_pay: toYuan(data.staff_pay),
        cleaner_pay: toYuan(data.cleaner_pay),
        water_pay: toYuan(data.water_pay),
        you_zan_pay: toYuan(data.you_zan_pay),
        other_pay: toYuan(data.other_pay),
        other_pay_remark: data.other_pay_remark || '',
        expenditure: toYuan(data.expenditure),
        balance: toYuan(data.balance),
        profit: toYuan(data.profit),
        store_deposit: toYuan(data.store_deposit),
      });
    },
    async getStaffSalaries() {
      const { isError, data } = await getStaffSalariesByReportId(this.id);

      if (isError) {
        this.isLoadingError = true;
        return;
      }

      data.forEach(v => {
        v.base_pay = toYuan(v.base_pay);
        v.percentage_pay = toYuan(v.percentage_pay);
        v.social_insurance = toYuan(v.social_insurance);
        v.personal_insurance = toYuan(v.personal_insurance);
        v.bonus = toYuan(v.bonus);
        v.actual_pay = toYuan(v.actual_pay);
        v.all_pay = toYuan(v.all_pay);
      });

      this.staffSalaries = data;
    },
    async getStoreIncomes() {
      const { isError, data } = await getStoreIncomesByReportId(this.id);

      if (isError) {
        this.isLoadingError = true;
        return;
      }

      data.forEach(v => {
        v.all_income = toYuan(v.all_income);
        v.profit = toYuan(v.profit);
        v.revenue_sharing = toYuan(v.revenue_sharing);
        v.commission = toYuan(v.commission);
      });

      this.storeIncomes = data;
    },
    async load() {
      if (this.isLoading) return;

      this.isLoading = true;
      this.isLoadingError = false;

      await Promise.all([
        this.show(),
        this.getStoreIncomes(),
        this.getStaffSalaries(),
      ]);

      this.isLoading = false;
    },
  },
};
</script>

<style src="../monthly-report/add.css" scoped/>
